import React, { useMemo } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

/**
 * Phone replacement for the resource time-grid on the Calendar page.
 *
 * The grid needs one ~270px column per provider, which cannot fit a 390px
 * screen (the page used to render nothing at all below 768px). This shows the
 * same events as a day agenda instead: a week strip, then one group per
 * provider with that day's bookings in time order. Same data and the same tap
 * behaviour as the grid (onOpen receives what BookingCalendar's eventClick
 * would: `{ details: extendedProps }`).
 *
 *   events        FullCalendar-style events built by CalendarPage
 *                 ({ id, title, resourceId, start, end, colors, extendedProps })
 *   selectedDate  "YYYY-MM-DD"
 */

const pad = (n) => String(n).padStart(2, "0");
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYmd = (s) => {
  const [y, m, d] = String(s || "").slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const addDays = (d, n) => {
  const next = new Date(d);
  next.setDate(next.getDate() + n);
  return next;
};
// "2026-10-11T14:30:00" -> "2:30 PM" (no Date parsing, so no timezone drift)
const clock = (iso) => {
  const hhmm = String(iso || "").slice(11, 16);
  const [h, m] = hhmm.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${pad(m)} ${suffix}`;
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const MobileDayAgenda = ({ events = [], selectedDate, onSelectDate, onOpen, bookingIdLabel = "Booking ID", providerLabel = "provider" }) => {
  const selected = parseYmd(selectedDate);
  const todayYmd = toYmd(new Date());
  const week = Array.from({ length: 7 }, (_, i) => addDays(selected, i - 3));

  // Group by provider; "Unassigned" first (still needs someone), then providers by their first booking.
  const groups = useMemo(() => {
    const byProvider = new Map();
    [...events]
      .sort((a, b) => String(a.start).localeCompare(String(b.start)))
      .forEach((ev) => {
        const key = String(ev.resourceId ?? "unassigned");
        if (!byProvider.has(key)) byProvider.set(key, { key, name: ev.title || "Unassigned", items: [] });
        byProvider.get(key).items.push(ev);
      });
    return [...byProvider.values()].sort((a, b) => {
      if (a.key === "unassigned") return -1;
      if (b.key === "unassigned") return 1;
      return String(a.items[0].start).localeCompare(String(b.items[0].start));
    });
  }, [events]);

  const total = new Set(events.map((e) => e.extendedProps?.booking_id ?? e.id)).size;
  const go = (n) => onSelectDate(toYmd(addDays(selected, n)));

  return (
    <div className="mb-4" data-tour="dashboard-calendar">
      {/* Week strip */}
      <div style={{ display: "flex", alignItems: "center", gap: "4px", margin: "4px 0 12px" }}>
        <button type="button" onClick={() => go(-7)} aria-label="Previous week" style={navBtn}>
          <MdChevronLeft size={22} />
        </button>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: "4px", flex: 1, minWidth: 0 }}>
          {week.map((d) => {
            const ymd = toYmd(d);
            const active = ymd === toYmd(selected);
            return (
              <button
                key={ymd}
                type="button"
                onClick={() => onSelectDate(ymd)}
                aria-pressed={active}
                aria-label={`${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`}
                style={{
                  minHeight: "56px",
                  padding: "6px 0",
                  borderRadius: "12px",
                  border: `1px solid ${active ? "#C2185B" : ymd === todayYmd ? "#C2185B55" : "#E3EBF8"}`,
                  background: active ? "linear-gradient(135deg, #C2185B 0%, #E91E63 100%)" : "#fff",
                  color: active ? "#fff" : "#111827",
                  cursor: "pointer",
                  lineHeight: 1.2,
                }}
              >
                <div style={{ fontSize: "10.5px", fontWeight: 500, opacity: active ? 0.9 : 0.6 }}>{WEEKDAYS[d.getDay()]}</div>
                <div style={{ fontSize: "16px", fontWeight: 700 }}>{d.getDate()}</div>
              </button>
            );
          })}
        </div>
        <button type="button" onClick={() => go(7)} aria-label="Next week" style={navBtn}>
          <MdChevronRight size={22} />
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 2px 10px" }}>
        <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>
          {WEEKDAYS[selected.getDay()]}, {selected.getDate()} {MONTHS[selected.getMonth()]}
          <span style={{ fontWeight: 400, color: "#6B7280" }}>
            {" "}
            · {total} {total === 1 ? "booking" : "bookings"}
          </span>
        </div>
        {toYmd(selected) !== todayYmd && (
          <button
            type="button"
            onClick={() => onSelectDate(todayYmd)}
            style={{ minHeight: "44px", padding: "0 6px", border: 0, background: "none", color: "#C2185B", fontWeight: 600, fontSize: "13px" }}
          >
            Today
          </button>
        )}
      </div>

      {groups.length === 0 && (
        <div
          style={{
            padding: "28px 16px",
            textAlign: "center",
            borderRadius: "14px",
            border: "1px dashed #D1D5DB",
            background: "#fff",
            color: "#6B7280",
            fontSize: "14px",
          }}
        >
          No bookings on this day.
          <div style={{ fontSize: "12.5px", marginTop: "4px" }}>Use the week strip to look at another day, or clear the {providerLabel} filter.</div>
        </div>
      )}

      {groups.map((group) => (
        <section key={group.key} aria-label={group.name} style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "0 2px 8px" }}>
            <span
              aria-hidden="true"
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: group.key === "unassigned" ? "#E5E7EB" : "#FDF2F5",
                color: group.key === "unassigned" ? "#6B7280" : "#C2185B",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "13px",
              }}
            >
              {(group.name || "?").charAt(0).toUpperCase()}
            </span>
            <span style={{ fontWeight: 700, fontSize: "15px", color: "#111827" }}>{group.name}</span>
            <span style={{ fontSize: "12px", color: "#6B7280" }}>{group.items.length}</span>
          </div>

          {group.items.map((ev) => {
            const p = ev.extendedProps || {};
            const service = [p.service_name, p.service_offering_name].filter(Boolean).join(" · ") || "Booking";
            return (
              <button
                key={ev.id}
                type="button"
                onClick={() => onOpen({ details: p })}
                style={{
                  display: "flex",
                  width: "100%",
                  gap: "12px",
                  alignItems: "stretch",
                  textAlign: "left",
                  minHeight: "64px",
                  marginBottom: "8px",
                  padding: "10px 12px",
                  borderRadius: "12px",
                  border: "1px solid #E3EBF8",
                  borderLeft: `4px solid ${ev.borderColor || "#999"}`,
                  background: "#fff",
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                <div style={{ flexShrink: 0, width: "68px", fontSize: "13px", fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>
                  {clock(ev.start)}
                  <div style={{ fontWeight: 400, fontSize: "11.5px", color: "#6B7280" }}>{clock(ev.end)}</div>
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827", overflowWrap: "anywhere" }}>{service}</div>
                  {p.user_name && <div style={{ fontSize: "13px", color: "#4B5563" }}>{p.user_name}</div>}
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                    {p.status_name && (
                      <span
                        style={{
                          padding: "2px 10px",
                          borderRadius: "999px",
                          fontSize: "11px",
                          fontWeight: 600,
                          background: ev.backgroundColor || "#F3F4F6",
                          color: ev.textColor || "#374151",
                          border: `1px solid ${ev.borderColor || "#D1D5DB"}`,
                        }}
                      >
                        {p.status_name}
                      </span>
                    )}
                    {p.display_booking_id && (
                      <span style={{ fontSize: "11.5px", color: "#6B7280" }}>
                        {bookingIdLabel} {p.display_booking_id}
                      </span>
                    )}
                    {p.resource_names && <span style={{ fontSize: "11.5px", color: "#6B7280" }}>{p.resource_names}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </section>
      ))}
    </div>
  );
};

const navBtn = {
  flexShrink: 0,
  width: "36px",
  minHeight: "56px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: 0,
  borderRadius: "10px",
  background: "transparent",
  color: "#4B5563",
  cursor: "pointer",
};

export default MobileDayAgenda;
